// ============================================
// SETTINGS PAGE - i18n
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Bell, Shield, Eye, Loader2, Save, Briefcase, Ship, DollarSign, Calendar, Globe, X, Key, AlertCircle, CheckCircle, Anchor, Trash2 } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { useMyProfile, useUpdatePreferences, useUpdateVisibility, useNotificationSettings, useUpdateNotificationSettings, useRanks, useDeleteAccount } from '@/hooks';
// Gemi türleri - getter trap bypass
const VESSEL_TYPES: Record<string, { tr: string; en: string }> = {
  BULK_CARRIER: { tr: 'Dökme Yük Gemisi', en: 'Bulk Carrier' },
  TANKER: { tr: 'Tanker', en: 'Tanker' },
  CONTAINER: { tr: 'Konteyner Gemisi', en: 'Container Ship' },
  GENERAL_CARGO: { tr: 'Genel Kargo', en: 'General Cargo' },
  RORO: { tr: 'Ro-Ro', en: 'Ro-Ro' },
  PASSENGER: { tr: 'Yolcu Gemisi', en: 'Passenger Ship' },
  OFFSHORE: { tr: 'Açık Deniz', en: 'Offshore' },
  TUG: { tr: 'Römorkör', en: 'Tug' },
  YACHT: { tr: 'Yat', en: 'Yacht' },
  FISHING: { tr: 'Balıkçı Gemisi', en: 'Fishing Vessel' },
  OTHER: { tr: 'Diğer', en: 'Other' },
};
import { authApi } from '@crew-at-sea/api-client';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const { t, i18n } = useTranslation(['panel', 'common']);
  const { user } = useAuthStore();
  const { data: profileResponse, isLoading: profileLoading } = useMyProfile();
  const { data: notifSettingsResponse } = useNotificationSettings();
  const { data: ranksResponse } = useRanks();
  
  const updatePreferences = useUpdatePreferences();
  const updateVisibility = useUpdateVisibility();
  const updateNotifSettings = useUpdateNotificationSettings();

  const profile: any = (profileResponse as any)?.data || profileResponse;
  const notifSettings: any = (notifSettingsResponse as any)?.data || notifSettingsResponse || {};
  const ranks: any[] = (ranksResponse as any)?.data || ranksResponse || [];

  const deleteAccountMutation = useDeleteAccount();

  // Delete Account Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  // Password Modal State
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [visibility, setVisibility] = useState({
    isPublic: true,
  });

  const [preferences, setPreferences] = useState({
    preferredVesselTypes: [] as string[],
    preferredFlags: [] as string[],
    preferredRankId: '',
    minSalary: '',
    maxSalary: '',
    preferredContractLength: '',
    willingToTravel: true,
    availableFrom: '',
    notes: '',
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    jobAlerts: true,
    applicationUpdates: true,
    marketingEmails: true,
  });

  // Initialize states from profile
  useEffect(() => {
    if (profile) {
      setVisibility({ isPublic: profile.isPublic ?? true });
      setPreferences({
        preferredVesselTypes: profile.preferredVesselTypes || [],
        preferredFlags: profile.preferredFlags || [],
        preferredRankId: profile.currentRankId || '',
        minSalary: profile.minSalary?.toString() || '',
        maxSalary: profile.maxSalary?.toString() || '',
        preferredContractLength: profile.preferredContractLength?.toString() || '',
        willingToTravel: profile.willingToTravel ?? true,
        availableFrom: profile.availableFrom ? profile.availableFrom.split('T')[0] : '',
        notes: profile.notes || '',
      });
    }
  }, [profile]);

  useEffect(() => {
    if (notifSettings) {
      setNotifications({
        emailNotifications: notifSettings.emailNotifications ?? true,
        jobAlerts: notifSettings.jobAlerts ?? true,
        applicationUpdates: notifSettings.applicationUpdates ?? true,
        marketingEmails: notifSettings.marketingEmails ?? true,
      });
    }
  }, [notifSettings]);

  const handleVisibilityChange = (isPublic: boolean) => {
    setVisibility({ isPublic });
    updateVisibility.mutate({ isPublic });
  };

  const handlePreferencesSave = () => {
    updatePreferences.mutate({
      preferredVesselTypes: preferences.preferredVesselTypes,
      preferredFlags: preferences.preferredFlags,
      minSalary: preferences.minSalary ? parseInt(preferences.minSalary) : undefined,
      maxSalary: preferences.maxSalary ? parseInt(preferences.maxSalary) : undefined,
      preferredContractLength: preferences.preferredContractLength ? parseInt(preferences.preferredContractLength) : undefined,
      willingToTravel: preferences.willingToTravel,
      availableFrom: preferences.availableFrom || undefined,
      notes: preferences.notes || undefined,
    });
  };

  const handleNotificationChange = (key: string, value: boolean) => {
    const newSettings = { ...notifications, [key]: value };
    setNotifications(newSettings);
    updateNotifSettings.mutate(newSettings);
  };

  const toggleVesselType = (type: string) => {
    setPreferences((prev) => ({
      ...prev,
      preferredVesselTypes: prev.preferredVesselTypes.includes(type)
        ? prev.preferredVesselTypes.filter((t) => t !== type)
        : [...prev.preferredVesselTypes, type],
    }));
  };

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="text-gray-500 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* Account Settings */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.account.title')}</h2>
          </div>
        </div>
        <div className="card-body space-y-4">
          <div>
            <label className="label">{t('settings.account.email')}</label>
            <input type="email" className="input bg-gray-50" value={user?.email || ''} disabled />
            <p className="text-xs text-gray-500 mt-1">{t('settings.account.emailHint')}</p>
          </div>
          <div>
            <label className="label">{t('settings.account.password')}</label>
            <button 
              className="btn-secondary"
              onClick={() => setShowPasswordModal(true)}
            >
              <Key className="w-4 h-4" />
              {t('settings.account.changePassword')}
            </button>
          </div>
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.privacy.title')}</h2>
          </div>
        </div>
        <div className="card-body">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">{t('settings.privacy.visibility')}</p>
              <p className="text-sm text-gray-500">{t('settings.privacy.visibilityHint')}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={visibility.isPublic}
                onChange={(e) => handleVisibilityChange(e.target.checked)}
                disabled={updateVisibility.isPending}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-maritime-600"></div>
            </label>
          </div>
          {updateVisibility.isPending && (
            <p className="text-xs text-maritime-600 mt-2 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> {t('common:saving')}
            </p>
          )}
        </div>
      </div>

      {/* Job Preferences */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.preferences.title')}</h2>
          </div>
        </div>
        <div className="card-body space-y-6">
          {/* Vessel Types */}
          <div>
            <label className="label flex items-center gap-2">
              <Ship className="w-4 h-4" />
              {t('settings.preferences.vesselTypes')}
            </label>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(VESSEL_TYPES).map(([key, labels]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleVesselType(key)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    preferences.preferredVesselTypes.includes(key)
                      ? 'bg-maritime-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {i18n.language === 'en' ? labels.en : labels.tr}
                </button>
              ))}
            </div>
          </div>

          {/* Preferred Rank */}
          <div>
            <label className="label flex items-center gap-2">
              <Anchor className="w-4 h-4" />
              {t('settings.preferences.preferredRank')}
            </label>
            <select
              className="input"
              value={preferences.preferredRankId}
              onChange={(e) => setPreferences({ ...preferences, preferredRankId: e.target.value })}
            >
              <option value="">{t('common:select')}</option>
              {ranks.map((rank: any) => (
                <option key={rank.id} value={rank.id}>
                  {i18n.language === 'en' ? (rank.nameEn || rank.nameTr) : (rank.nameTr || rank.nameEn)}
                </option>
              ))}
            </select>
          </div>

          {/* Salary Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                {t('settings.preferences.minSalary')}
              </label>
              <input
                type="number"
                className="input"
                placeholder="3000"
                value={preferences.minSalary}
                onChange={(e) => setPreferences({ ...preferences, minSalary: e.target.value })}
              />
            </div>
            <div>
              <label className="label">{t('settings.preferences.maxSalary')}</label>
              <input
                type="number"
                className="input"
                placeholder="8000"
                value={preferences.maxSalary}
                onChange={(e) => setPreferences({ ...preferences, maxSalary: e.target.value })}
              />
            </div>
          </div>

          {/* Contract Length */}
          <div>
            <label className="label flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t('settings.preferences.contractLength')}
            </label>
            <select
              className="input"
              value={preferences.preferredContractLength}
              onChange={(e) => setPreferences({ ...preferences, preferredContractLength: e.target.value })}
            >
              <option value="">{t('common:select')}</option>
              <option value="3">{t('settings.preferences.months', { count: 3 })}</option>
              <option value="4">{t('settings.preferences.months', { count: 4 })}</option>
              <option value="5">{t('settings.preferences.months', { count: 5 })}</option>
              <option value="6">{t('settings.preferences.months', { count: 6 })}</option>
              <option value="8">{t('settings.preferences.months', { count: 8 })}</option>
              <option value="9">{t('settings.preferences.months', { count: 9 })}</option>
              <option value="12">{t('settings.preferences.months', { count: 12 })}</option>
            </select>
          </div>

          {/* Available From */}
          <div>
            <label className="label flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t('settings.preferences.availableFrom')}
            </label>
            <input
              type="date"
              className="input"
              value={preferences.availableFrom}
              onChange={(e) => setPreferences({ ...preferences, availableFrom: e.target.value })}
            />
          </div>

          {/* Willing to Travel */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                {t('settings.preferences.willingToTravel')}
              </p>
              <p className="text-sm text-gray-500">{t('settings.preferences.willingToTravelHint')}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={preferences.willingToTravel}
                onChange={(e) => setPreferences({ ...preferences, willingToTravel: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-maritime-600"></div>
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="label">{t('settings.preferences.notes')}</label>
            <textarea
              className="input"
              rows={3}
              placeholder={t('settings.preferences.notesPlaceholder')}
              value={preferences.notes}
              onChange={(e) => setPreferences({ ...preferences, notes: e.target.value })}
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
              onClick={handlePreferencesSave}
              className="btn-primary"
              disabled={updatePreferences.isPending}
            >
              {updatePreferences.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" />{t('common:saving')}</>
              ) : (
                <><Save className="w-4 h-4" />{t('settings.preferences.save')}</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.notifications.title')}</h2>
          </div>
        </div>
        <div className="card-body space-y-4">
          <NotificationToggle
            label={t('settings.notifications.email')}
            description={t('settings.notifications.emailDesc')}
            checked={notifications.emailNotifications}
            onChange={(checked) => handleNotificationChange('emailNotifications', checked)}
            loading={updateNotifSettings.isPending}
          />
          <NotificationToggle
            label={t('settings.notifications.jobAlerts')}
            description={t('settings.notifications.jobAlertsDesc')}
            checked={notifications.jobAlerts}
            onChange={(checked) => handleNotificationChange('jobAlerts', checked)}
            loading={updateNotifSettings.isPending}
          />
          <NotificationToggle
            label={t('settings.notifications.applicationUpdates')}
            description={t('settings.notifications.applicationUpdatesDesc')}
            checked={notifications.applicationUpdates}
            onChange={(checked) => handleNotificationChange('applicationUpdates', checked)}
            loading={updateNotifSettings.isPending}
          />
          <NotificationToggle
            label={t('settings.notifications.marketing')}
            description={t('settings.notifications.marketingDesc')}
            checked={notifications.marketingEmails}
            onChange={(checked) => handleNotificationChange('marketingEmails', checked)}
            loading={updateNotifSettings.isPending}
          />
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card border border-red-200 bg-red-50/10 rounded-2xl overflow-hidden">
        <div className="border-b border-red-100 bg-red-50/30 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-red-950">Tehlikeli Bölge</h2>
              <p className="text-xs text-red-700 mt-0.5">Hesap yönetimiyle ilgili geri alınamaz işlemler</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-red-950">Hesabı Kalıcı Olarak Sil</p>
              <p className="text-sm text-red-700 max-w-xl mt-1">
                Hesabınızı sildiğinizde, profil bilgileriniz, sertifikalarınız, geçmiş iş başvurularınız ve yüklediğiniz tüm belgeler kalıcı olarak silinir. Bu işlem geri alınamaz.
              </p>
            </div>
            <button
              onClick={handleDeleteAccount}
              disabled={deleteAccountMutation.isPending}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors shadow-sm disabled:opacity-50 flex-shrink-0 w-full sm:w-auto"
            >
              {deleteAccountMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Hesabımı Sil
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} t={t} />
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => deleteAccountMutation.mutate()}
          registeredEmail={user?.email || ''}
          isPending={deleteAccountMutation.isPending}
        />
      )}
    </div>
  );
}

function NotificationToggle({ label, description, checked, onChange, loading }: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  loading?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={loading}
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-maritime-600"></div>
      </label>
    </div>
  );
}

// ============================================
// CHANGE PASSWORD MODAL - i18n
// ============================================
function ChangePasswordModal({ onClose, t }: { onClose: () => void; t: (key: string) => string }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      setMessage({ type: 'success', text: t('settings.password.success') });
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        onClose();
      }, 2000);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || t('settings.password.error');
      setMessage({ type: 'error', text: errorMessage });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!formData.currentPassword) {
      setMessage({ type: 'error', text: t('settings.password.currentRequired') });
      return;
    }

    if (!formData.newPassword) {
      setMessage({ type: 'error', text: t('settings.password.newRequired') });
      return;
    }

    if (formData.newPassword.length < 8) {
      setMessage({ type: 'error', text: t('settings.password.minLength') });
      return;
    }

    const hasUppercase = /[A-Z]/.test(formData.newPassword);
    const hasLowercase = /[a-z]/.test(formData.newPassword);
    const hasNumber = /\d/.test(formData.newPassword);
    
    if (!hasUppercase || !hasLowercase || !hasNumber) {
      setMessage({ type: 'error', text: t('settings.password.complexity') });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: t('settings.password.mismatch') });
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-maritime-100 flex items-center justify-center">
              <Key className="w-5 h-5 text-maritime-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">{t('settings.password.title')}</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {message && (
          <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.password.current')}
            </label>
            <input
              type="password"
              value={formData.currentPassword}
              onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
              className="input w-full"
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.password.new')}
            </label>
            <input
              type="password"
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              className="input w-full"
              placeholder="••••••••"
              autoComplete="new-password"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('settings.password.hint')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.password.confirm')}
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="input w-full"
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={changePasswordMutation.isPending}
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-maritime-600 text-white rounded-lg hover:bg-maritime-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              disabled={changePasswordMutation.isPending}
            >
              {changePasswordMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('settings.password.updating')}
                </>
              ) : (
                <>
                  <Key className="w-4 h-4" />
                  {t('settings.password.update')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// DELETE ACCOUNT MODAL
// ============================================
function DeleteAccountModal({ onClose, onConfirm, registeredEmail, isPending }: { 
  onClose: () => void; 
  onConfirm: () => void; 
  registeredEmail: string;
  isPending: boolean;
}) {
  const [emailInput, setEmailInput] = useState('');
  const isEmailMatch = emailInput.trim().toLowerCase() === registeredEmail.toLowerCase();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEmailMatch && !isPending) {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 border border-slate-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Hesabı Sil</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex gap-3 text-red-800 text-sm mb-6">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-semibold">Bu işlem geri alınamaz!</p>
            <p className="leading-relaxed">Hesabınızla birlikte tüm başvurularınız, sertifikalarınız ve kayıtlı tüm bilgileriniz kalıcı olarak silinecektir.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Lütfen onaylamak için kayıtlı e-posta adresinizi (<span className="font-mono text-slate-900 select-all">{registeredEmail}</span>) yazın:
            </label>
            <input
              type="email"
              required
              autoFocus
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-sm font-mono"
              placeholder="e-posta@adresiniz.com"
              disabled={isPending}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors text-sm"
              disabled={isPending}
            >
              İptal
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
              disabled={!isEmailMatch || isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Hesabımı Sil
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
